import urls from '@constants/urls';

export const LOCK_NAME = 'sso-login';
export const CHANNEL_NAME = 'sso-login-channel';
export const LEADER_KEY = 'sso-login-in-progress';
export const STALE_MS = 60 * 1000;

const TAB_ID_KEY = 'sso-tab-id';
const WINDOW_NAME_PREFIX = 'sso-tab-id:';
const SAVED_ORIGIN_PATHNAME_KEY = 'sso-origin-pathname';

const generateTabId = () => {
    if ('undefined' === typeof window || !window.crypto?.randomUUID) {
        throw new Error('crypto.randomUUID is required');
    }

    return `${Date.now()}-${window.crypto.randomUUID()}`;
};

export const canUseBroadcastChannel = () => {
    return 'undefined' !== typeof window && Boolean(window.BroadcastChannel);
};

export const canUseSsoCoordinator = () => {
    if ('undefined' === typeof window || 'undefined' === typeof navigator) {
        return false;
    }

    return Boolean(navigator.locks?.request && canUseBroadcastChannel());
};

const getWindowTabId = () => {
    if ('undefined' === typeof window || 'string' !== typeof window.name) {
        return null;
    }

    if (0 !== window.name.indexOf(WINDOW_NAME_PREFIX)) {
        return null;
    }

    const id = window.name.slice(WINDOW_NAME_PREFIX.length);

    return id || null;
};

const setWindowTabId = (id) => {
    if ('undefined' !== typeof window) {
        window.name = `${WINDOW_NAME_PREFIX}${id}`;
    }
};

const setTabId = (id) => {
    sessionStorage.setItem(TAB_ID_KEY, id);
    setWindowTabId(id);

    return id;
};

export const getOrCreateTabId = () => {
    const sessionId = sessionStorage.getItem(TAB_ID_KEY);
    const windowId = getWindowTabId();

    if (sessionId && windowId && sessionId === windowId) {
        return sessionId;
    }

    // Duplicated tabs can inherit sessionStorage while window.name is reset.
    return setTabId(generateTabId());
};

export const setLeaderInfo = ({id, timestamp}) => {
    localStorage.setItem(
        LEADER_KEY,
        JSON.stringify({
            id,
            timestamp,
        })
    );
};

export const getLeaderInfo = () => {
    const rawLeader = localStorage.getItem(LEADER_KEY);

    if (!rawLeader) {
        return null;
    }

    try {
        const parsed = JSON.parse(rawLeader);

        if (!parsed || 'string' !== typeof parsed.id || 'number' !== typeof parsed.timestamp) {
            return null;
        }

        return parsed;
    } catch {
        return null;
    }
};

export const clearLeaderInfo = () => {
    localStorage.removeItem(LEADER_KEY);
};

export const isValidDoneUserPayload = (user) => {
    if (!user || 'object' !== typeof user) {
        return false;
    }

    const hasId = null !== user.id && 'undefined' !== typeof user.id;

    return hasId && Array.isArray(user.resources);
};

const getDoneUserPayload = (user) => {
    return isValidDoneUserPayload(user) ? user : null;
};

export const broadcastDone = (user) => {
    if (!canUseBroadcastChannel()) {
        return;
    }

    const leader = getLeaderInfo();

    if (!leader) {
        return;
    }

    clearLeaderInfo();

    try {
        const channel = new window.BroadcastChannel(CHANNEL_NAME);

        channel.postMessage({
            type: 'done',
            id: leader.id,
            user: getDoneUserPayload(user),
        });
        channel.close();
    } catch {
        // Ignore browser/runtime channel errors.
    }
};

export const isLeaderExpired = (leader, staleMs = STALE_MS) => {
    if (!leader || 'number' !== typeof leader.timestamp) {
        return true;
    }

    return Date.now() - leader.timestamp > staleMs;
};

export const getSavedOriginPathname = (tabId) => {
    if ('undefined' === typeof window || 'string' !== typeof tabId) {
        return null;
    }

    const key = `${SAVED_ORIGIN_PATHNAME_KEY}-${tabId}`;

    try {
        const saved = sessionStorage.getItem(key);

        return saved || null;
    } catch {
        return null;
    }
};

export const setSavedOriginPathname = (tabId, pathname) => {
    if ('undefined' === typeof window || 'string' !== typeof tabId || 'string' !== typeof pathname) {
        return null;
    }

    if (!pathname) {
        return null;
    }

    const key = `${SAVED_ORIGIN_PATHNAME_KEY}-${tabId}`;

    try {
        sessionStorage.setItem(key, pathname);

        return pathname;
    } catch {
        return null;
    }
};

export const deleteSavedOriginPathname = (tabId) => {
    if ('undefined' === typeof window || 'string' !== typeof tabId) {
        return false;
    }

    const key = `${SAVED_ORIGIN_PATHNAME_KEY}-${tabId}`;

    try {
        sessionStorage.removeItem(key);

        return true;
    } catch {
        return false;
    }
};

export const shouldRestorePathname = (currentPathname, savedPathname) => {
    if ('string' !== typeof currentPathname || 'string' !== typeof savedPathname) {
        return false;
    }

    // Compare just the pathname part (without search or hash)
    const currentPath = currentPathname.split('?')[0].split('#')[0];
    const savedPath = savedPathname.split('?')[0].split('#')[0];

    return currentPath !== savedPath;
};

export const restoreOriginPathnameIfNeeded = (tabId, currentPathname) => {
    if ('string' !== typeof tabId || 'string' !== typeof currentPathname) {
        return null;
    }

    try {
        const savedPathname = getSavedOriginPathname(tabId);

        deleteSavedOriginPathname(tabId);

        if (!savedPathname) {
            return null;
        }

        if (!shouldRestorePathname(currentPathname, savedPathname)) {
            return null;
        }

        return savedPathname;
    } catch {
        return null;
    }
};

export const redirectToLogin = (location = window.location) => {
    const tabId = getOrCreateTabId();
    const originPathname = location.pathname + location.search + location.hash;

    setSavedOriginPathname(tabId, originPathname);

    const fromUrl = urls.authLogin + encodeURIComponent(originPathname);

    location.replace(fromUrl);
};

export const redirectToLogout = (location = window.location) => {
    const tabId = getOrCreateTabId();
    const originPathname = location.pathname + location.search + location.hash;

    setSavedOriginPathname(tabId, originPathname);

    if (canUseSsoCoordinator()) {
        setLeaderInfo({
            id: tabId,
            timestamp: Date.now(),
        });
    }

    location.href = urls.authLogout;
};
